/**
 * Vesper Cleo Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bust } from './dedupedFetch';
import type { ArkChatRequest, ArkChatResponse, ArkResumeResponse } from '../types/ark.types';

export interface RatingSessionPayload {
  peers: { rateeId: string; score: number; noShow?: boolean }[];
  eventScore: number;
  vibeResponse?: 'yes' | 'neutral' | 'no';
}

export interface BenchMember {
  userId: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
  trustScore: number;
  similarityScore: number;
}

export async function sendArkMessage(payload: ArkChatRequest): Promise<ArkChatResponse> {
  const res = await apiClient.post<{ data: ArkChatResponse }>(API.ARK_CHAT, payload);
  return res.data.data;
}

export async function submitRatingSession(
  eventId: string,
  payload: RatingSessionPayload,
): Promise<void> {
  await apiClient.post(API.ARK_RATE_SESSION(eventId), payload);
}

export async function getMyRatings(): Promise<unknown[]> {
  const res = await apiClient.get<{ data: unknown[] }>(API.MY_RATINGS);
  return res.data.data;
}

/** Surfaces that originate a connection request. The backend writes this
 *  through to friend_requests.source + (on mutual) friend_bench.initiator_source
 *  so the "Intros delivered" Trust Score axis can count the right thing. Only
 *  'feed' and 'horizon' currently count toward that axis (per migration 070). */
export type ConnectSource = 'feed' | 'horizon' | 'profile' | 'event' | 'cleo' | 'other';

export async function sendFriendRequest(
  targetId: string,
  sourceEventId?: string,
  source?: ConnectSource,
): Promise<{ mutual: boolean }> {
  const res = await apiClient.post<{ data: { mutual: boolean } }>(
    API.FRIEND_REQUEST,
    { targetId, sourceEventId, source },
  );
  // A mutual accept adds to bench; non-mutual changes incoming/sent lists.
  // A non-mutual send also consumes a cold-connect (per Phase 6), so the
  // quota count needs invalidation.
  bust('me:bench');
  bust('me:incomingRequests');
  bust('me:coldConnectQuota');
  return res.data.data;
}

export async function getMyBench(): Promise<BenchMember[]> {
  return deduped('me:bench', async () => {
    const res = await apiClient.get<{ data: { bench: BenchMember[] } }>(API.MY_BENCH);
    return res.data.data.bench;
  });
}

export interface FriendRequest {
  id: string;
  requester_id?: string;
  target_id?: string;
  source_event: string | null;
  created_at: string;
  display_name: string;
  username: string;
  avatar_url: string | null;
  trust_score: number;
}

export async function getIncomingRequests(): Promise<FriendRequest[]> {
  return deduped('me:incomingRequests', async () => {
    const res = await apiClient.get<{ data: { requests: FriendRequest[] } }>(API.FRIEND_INCOMING);
    return res.data.data.requests;
  });
}

export async function getSentRequests(): Promise<FriendRequest[]> {
  const res = await apiClient.get<{ data: { requests: FriendRequest[] } }>(API.FRIEND_SENT);
  return res.data.data.requests;
}

export async function declineFriendRequest(requestId: string): Promise<void> {
  await apiClient.delete(API.FRIEND_DECLINE(requestId));
  bust('me:incomingRequests');
}

export async function removeFriend(friendId: string): Promise<void> {
  await apiClient.delete(API.FRIEND_REMOVE(friendId));
  bust('me:bench');
}

/** Vesper Phase 6: cold-connect quota status for the current user. */
export interface ColdConnectQuota {
  used: number;
  limit: number;
  /** ISO 8601 — next midnight UTC; rolling 24h count, this is a UX cue only */
  resetsAt: string;
}

export async function getColdConnectQuota(): Promise<ColdConnectQuota> {
  return deduped('me:coldConnectQuota', async () => {
    const res = await apiClient.get<{ data: ColdConnectQuota }>(API.COLD_CONNECT_QUOTA);
    return res.data.data;
  });
}

export async function selectExternalVenue(
  planningSessionId: string,
  selection: Record<string, unknown>,
): Promise<void> {
  await apiClient.post(API.ARK_SELECT_EXTERNAL, { planningSessionId, selection });
}

export async function skipExternalSearch(
  planningSessionId: string,
): Promise<void> {
  await apiClient.post(API.ARK_SKIP_EXTERNAL, { planningSessionId });
}

export async function selectVenueFromSuggestions(
  sessionId: string,
  placeId: string,
): Promise<void> {
  await apiClient.post(API.ARK_SELECT_VENUE, { planningSessionId: sessionId, placeId });
}

export async function rejectVenueSuggestions(
  sessionId: string,
): Promise<void> {
  await apiClient.post(API.ARK_REJECT_VENUES, { planningSessionId: sessionId });
}

export async function abandonSession(
  sessionId: string,
): Promise<void> {
  await apiClient.post(API.ARK_ABANDON_SESSION, { planningSessionId: sessionId });
}

/** Abandon whatever active/completed session the current user has (no ID needed) */
export async function abandonActiveSession(): Promise<void> {
  await apiClient.post(API.ARK_ABANDON_ACTIVE_SESSION);
}

export async function completeSession(
  sessionId: string,
  eventPromptId?: string,
): Promise<void> {
  await apiClient.post(API.ARK_COMPLETE_SESSION, { planningSessionId: sessionId, eventPromptId });
}

export async function resumeSession(): Promise<ArkResumeResponse | null> {
  const res = await apiClient.get<{ data: ArkResumeResponse | null }>(API.ARK_SESSION);
  return res.data.data;
}
